// frontend/src/index.js

import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import { preloadAuthBootLanding, preloadRouteForPath } from "./appRoutes";
import { normalizeIndexHtmlLocation } from "./utils/indexHtmlUrl";
import { captureAcquisitionParams } from "./utils/acquisitionCapture";

// Create gradient background elements
const createGradientBackground = () => {
  // Guard against duplication: prerendered pages (scripts/prerender.js) already
  // contain this node in their HTML snapshot, so skip if it's present.
  if (document.querySelector(".gradient-bg")) return;

  const gradientBg = document.createElement("div");
  gradientBg.className = "gradient-bg";

  // Create gradient orbs
  for (let i = 1; i <= 4; i++) {
    const orb = document.createElement("div");
    orb.className = `gradient-orb gradient-orb-${i}`;
    gradientBg.appendChild(orb);
  }

  // Insert before root element
  document.body.insertBefore(gradientBg, document.getElementById("root"));
};

// The static host 301s a no-slash hard load of a prerendered route
// (`/register?s=k8`) to `/register/index.html?s=k8` (backlog #465). Rewrite the
// address to `/register/?s=k8` NOW, synchronously, before anything reads it:
// the router, CaptureAcquisition and every analytics lane then start from the
// real route instead of an unmatched `/index.html` path (which rendered no
// route and counted page views on a path that does not exist). Query, hash and
// history.state are kept; it never throws.
normalizeIndexHtmlLocation();

// Store the landing URL's acquisition codes (?s= ?c= ?src= ?ref=) NOW, before
// React exists - validated, first writer wins, nothing sent and the URL left
// as it is (utils/acquisitionCapture.js). A prerendered page is clickable from
// first paint, but React - and so CaptureAcquisition's effect - starts only
// once the route chunk is in (below, up to HYDRATION_PRELOAD_TIMEOUT_MS); a
// snapshot link tapped in that window is a native navigation that would
// otherwise take the paid-ad code or the friend code with it. After the
// index.html rewrite, so it reads the clean address; CaptureAcquisition runs
// the same capture again (a no-op) and still strips the URL and sends
// utm_visit.
captureAcquisitionParams();

// Initialize gradient background
createGradientBackground();

// AN AUTHENTICATED BOOT OF "/" (window.__LF_AUTH_BOOT__, set by the head script
// in public/index.html - backlog #158). Two things, both before the first
// render, both only on that boot - an anonymous visitor's load is untouched:
//
//   - start the landing page's chunk now, in parallel with the session check,
//     so it is not fetched only after GET /auth/session resolves - when the
//     boot lands on the Dashboard ("/" or /dashboard). The pathname read here
//     is already the real address: a deep link booted through 404.html at "/"
//     (where the head script saw "/") is restored by public/index.html's
//     end-of-body shim before this deferred bundle runs. Only the chunk - the
//     session itself is never prefetched (appRoutes.js);
//   - drop the `hl-page-bg` body class the prerendered "/" snapshot bakes in.
//     The homepage adds and removes it itself (homepage/useLiveMeshPageChrome
//     .js), but this visitor is redirected before Homepage ever mounts, so
//     nothing would ever remove it: the homepage ground colour would paint
//     behind the app for the whole session. A later visit to the homepage in
//     this session adds it again through the same hook.
if (window.__LF_AUTH_BOOT__ === true) {
  preloadAuthBootLanding(window.location.pathname);
  try {
    document.body.classList.remove("hl-page-bg");
  } catch (e) {
    /* No body classList: nothing was painted through it either. */
  }
}

// NO native-submit guard here: public/index.html installs it at parse time
// (the "NATIVE-SUBMIT GUARD" head script), before any prerendered form exists
// - a listener added here would arrive after main.js, too late for an Enter
// pressed while the bundle downloads. That head script is the only copy.

const container = document.getElementById("root");
const app = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If the container already has markup, it was prerendered (scripts/prerender.js)
// — hydrate it so React attaches to the existing DOM instead of throwing it away.
// Otherwise this is a normal client-only boot.
//
// The prerender emits React Suspense boundary markers (<!--$--> ... <!--/$-->)
// around the route content, so React can claim the route-level <Suspense> in
// App.js and hydrate the snapshot in place instead of discarding it - on
// every route EXCEPT "/", which is deliberately exempt until backlog #469
// (src/prerender/hydrationMarkers.js ROUTE_MARKER_DEFERRED): its snapshot ships unmarked,
// so "/" keeps its pre-#464 boot - without the markers React reports #418,
// throws the WHOLE root away and client-renders it (the homepage is imported
// eagerly, so no loading spinner shows in between). Any
// mismatch that remains is recoverable (React re-renders that subtree on the
// client) and is surfaced through the `[hydration]` console warning below: the
// error message (a minified React error code in production builds) and the
// component stack only - nothing is added from the location, the query string
// or the user.
//
// EXCEPT on an authenticated boot of "/" (window.__LF_AUTH_BOOT__, set by the
// head script in public/index.html - backlog #158): that visitor is on the "/"
// homepage snapshot but will render an app route (/dashboard, or the deep link
// the body shim restored), so hydrating 322KB of marketing DOM React will
// immediately discard is pure cost. createRoot() renders over it instead. The
// snapshot deliberately stays in the document until React's first commit — it
// is the fallback the 4s failsafe reveals if React never mounts.
//
// THE ROUTE'S PAGE MODULE IS LOADED BEFORE hydrateRoot (#464). A route boundary
// that suspends while hydrating stays dehydrated until its chunk arrives, and
// React 19 drops it - committing PageLoadingFallback over the snapshot - as
// soon as any context above it updates in that window (the /pricing reply,
// a stored consent, the auth check). So the boundary must never suspend:
// preloadRouteForPath (appRoutes.js, the same route table <App> renders) starts
// the page module, and hydration waits for it. The snapshot stays on screen the
// whole time - what the visitor sees does not change, only when React takes
// over. The chunk is normally already downloading: the prerender puts a
// `<link rel="preload">` for it in the page's <head>, in parallel with this
// bundle. "/" (eager Homepage) and every route without a preloadable page get
// null and hydrate at once, exactly as before.
const hydrate = () => {
  hydrateRoot(container, app, {
    onRecoverableError: (error, errorInfo) => {
      console.warn(
        "[hydration]",
        error && error.message,
        errorInfo && errorInfo.componentStack
      );
    },
  });
};

// The longest hydration waits for the page module. Past it (a chunk still
// downloading on a bad connection) hydrate anyway - the route may then show the
// loading spinner until the chunk lands. Long enough for a slow mobile
// connection to fetch a page chunk that has been downloading since <head> was
// parsed; short enough that the consent banner, the session check and the
// page view are never held for long. A chunk that FAILS is not a timeout - see
// recoverFromFailedRouteChunk below.
const HYDRATION_PRELOAD_TIMEOUT_MS = 4000;

/**
 * How the page-module wait ended: "loaded", "failed" (the import rejected) or
 * "timeout" (still pending after `ms`). Never rejects, and resolves exactly
 * once - a module that settles after the timeout is ignored here.
 *
 * @param {Promise<unknown>} promise
 * @param {number} ms
 * @returns {Promise<"loaded" | "failed" | "timeout">}
 */
const waitForRouteModule = (promise, ms) =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve("timeout"), ms);
    const settle = (outcome) => () => {
      clearTimeout(timer);
      resolve(outcome);
    };
    promise.then(settle("loaded"), settle("failed"));
  });

// A route chunk that FAILS to load (#464). The usual cause is a deploy: the
// visitor holds an HTML page from the previous build (an old tab, a
// discarded tab restored from disk cache) whose chunk names no longer exist on the bucket. Hydrating anyway
// would re-request the same missing chunk from the lazy route, fail again with
// no error boundary above it, and React would unmount the whole root - a
// BLANK page. So instead:
//   - first failure for this path in this tab: reload once. index.html is
//     served no-cache, so the reload fetches the current build's HTML and
//     chunk names;
//   - a second failure (the flag is still set): do NOT hydrate. The static
//     snapshot stays exactly as painted - readable, its links work as plain
//     links, and the head-script submit guard keeps its forms inert;
//   - a page whose module EVENTUALLY loads clears the flag - also when it
//     lands after the hydration timeout - so a later deploy can use its one
//     reload again;
//   - offline (navigator.onLine === false), no reload at all: the reload
//     would replace a readable snapshot with the browser's offline error
//     page, and the flag stays free for when the connection is back.
// The flag is per path and per tab (sessionStorage). The guard FAILS CLOSED:
// if storage cannot be read, the tab is treated as having already reloaded,
// and if it cannot be written, there is no loop guard - either way no reload,
// the snapshot stays. Nothing about the visitor or the error is logged.
const ROUTE_CHUNK_RELOAD_KEY_PREFIX = "lf_route_chunk_reload:";

const routeChunkReloadKey = (pathname) => `${ROUTE_CHUNK_RELOAD_KEY_PREFIX}${pathname}`;

/**
 * True when this tab already reloaded once for `pathname` - and also when
 * storage cannot be read (fail closed: an unreadable flag must never allow a
 * reload loop).
 */
const hasReloadedForRouteChunk = (pathname) => {
  try {
    return window.sessionStorage.getItem(routeChunkReloadKey(pathname)) !== null;
  } catch (e) {
    return true;
  }
};

/** Record the one reload; false when storage refuses (then never reload). */
const markRouteChunkReload = (pathname) => {
  try {
    window.sessionStorage.setItem(routeChunkReloadKey(pathname), "1");
    return true;
  } catch (e) {
    return false;
  }
};

const clearRouteChunkReload = (pathname) => {
  try {
    window.sessionStorage.removeItem(routeChunkReloadKey(pathname));
  } catch (e) {
    /* storage unavailable - nothing was stored either */
  }
};

/** True only when the browser positively reports no connection; unknown counts as online. */
const isOffline = () =>
  typeof navigator !== "undefined" && navigator.onLine === false;

const recoverFromFailedRouteChunk = (pathname) => {
  if (isOffline()) {
    console.warn("[hydration] route chunk failed to load while offline - keeping the static page");
    return;
  }
  if (!hasReloadedForRouteChunk(pathname) && markRouteChunkReload(pathname)) {
    console.warn("[hydration] route chunk failed to load - reloading once");
    window.location.reload();
    return;
  }
  console.warn("[hydration] route chunk failed to load - keeping the static page");
};

const bootPrerendered = () => {
  const { pathname } = window.location;
  const pageModule = preloadRouteForPath(pathname);
  if (!pageModule) {
    hydrate();
    return;
  }
  // Whenever the module loads - before the timeout or after it - this tab's
  // one reload for the path is spent no longer. Its own handler, so a module
  // that lands after hydration has already started still clears the flag; a
  // rejection here is the waitForRouteModule branch's to handle.
  pageModule.then(
    () => clearRouteChunkReload(pathname),
    () => {}
  );
  waitForRouteModule(pageModule, HYDRATION_PRELOAD_TIMEOUT_MS).then((outcome) => {
    if (outcome === "failed") {
      recoverFromFailedRouteChunk(pathname);
      return;
    }
    hydrate();
  });
};

if (container.hasChildNodes() && !window.__LF_AUTH_BOOT__) {
  bootPrerendered();
} else {
  // UNDER THE PRERENDERER (#464), start the route's page module BEFORE the
  // first render - the same call, with the same path, a visitor's boot makes
  // above. scripts/prerender.js records the chunk <script>s webpack adds and
  // preloads the FIRST batch in the snapshot's <head>; starting the import
  // here makes that batch, by construction, exactly what the visitor's
  // index.js will wait for (not whatever the render happened to request
  // first). Whether the route HAS such a page is reported too, so the
  // prerender can fail a route that has one but recorded no chunk. Visitors
  // never take this branch with the flag set - it is the prerenderer's own.
  if (window.__PRERENDER__ === true) {
    const pageModule = preloadRouteForPath(window.location.pathname);
    window.__PRERENDER_ROUTE_PRELOAD__ = pageModule !== null;
    // A failure surfaces through the route's own lazy render (an empty
    // snapshot the prerender fails on); this copy of the promise stays quiet.
    if (pageModule) pageModule.catch(() => {});
  }
  createRoot(container).render(app);
}

// Register Service Worker for PWA functionality
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js');
  });
}
